import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Calendar, Users, DollarSign, Package, TrendingUp } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

// Helpers for date-range presets (ISO YYYY-MM-DD).
const toISODate = (d) => d.toISOString().split('T')[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toISODate(d); };
const today = () => toISODate(new Date());

const PRESETS = [
  { label: 'Last 7 days',  start: () => daysAgo(7),  end: today },
  { label: 'Last 30 days', start: () => daysAgo(30), end: today },
  { label: 'Last 90 days', start: () => daysAgo(90), end: today },
  { label: 'Year to date', start: () => `${new Date().getFullYear()}-01-01`, end: today },
];

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div style={{
    background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: 16, display: 'flex', gap: 12, alignItems: 'center',
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10, background: `${accent}20`, color: accent,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}><Icon size={20} /></div>
    <div>
      <p style={{ fontSize: 11, color: '#64748b', margin: 0, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '2px 0 0' }}>{value}</p>
    </div>
  </div>
);

const CompletedOrdersReport = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [customer, setCustomer] = useState('');
  const [operator, setOperator] = useState('all');
  const [data, setData] = useState({ orders: [], summary: {}, by_operator: [], by_customer: [], available_operators: [] });
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (customer.trim()) params.append('customer', customer.trim());
      if (operator && operator !== 'all') params.append('operator', operator);
      const r = await axios.get(`/orders/reports/completed?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally { setLoading(false); }
  };

  // Initial load + refetch on filter changes (debounced by user-driven Apply button below would be cleaner,
  // but date/operator changes are infrequent enough that direct fetch keeps the UI snappy and predictable).
  useEffect(() => { fetchReport(); }, [startDate, endDate, operator]); // eslint-disable-line

  const applyCustomerFilter = (e) => { e.preventDefault(); fetchReport(); };

  // Build CSV client-side — small payloads (max 2000 orders) so this stays fast.
  const exportCsv = () => {
    const rows = data.orders || [];
    if (rows.length === 0) { toast.error('Nothing to export'); return; }
    const header = ['PO Number', 'Customer', 'Quantity', 'Total', 'Completed At', 'Completed By', 'Status'];
    const lines = [header.join(',')];
    rows.forEach((o) => {
      const cols = [
        o.po_number || o.id,
        (o.customer_name || '').replaceAll('"', '""'),
        o.quantity || 0,
        (o.total_amount || 0),
        o.completed_at || '',
        o.completed_by || '',
        o.status || '',
      ].map((v) => `"${v}"`);
      lines.push(cols.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed_orders_${startDate || 'all'}_${endDate || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const summary = data.summary || {};
  const operators = useMemo(() => ['all', ...(data.available_operators || [])], [data.available_operators]);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <AppHeader user={user} onLogout={logout} />

      <main style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 800, margin: 0, color: '#0f172a' }}>Completed Orders Report</h1>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
              Filter by date, customer, or operator. Export to CSV for accounting.
            </p>
          </div>
          <Button onClick={exportCsv} data-testid="export-csv-btn" style={{ background: '#0f172a', color: '#fff' }}>
            <Download size={14} style={{ marginRight: 6 }} />Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {PRESETS.map((p) => (
              <button key={p.label} type="button"
                onClick={() => { setStartDate(p.start()); setEndDate(p.end()); }}
                data-testid={`preset-${p.label.toLowerCase().replaceAll(' ', '-')}`}
                style={{
                  all: 'unset', cursor: 'pointer',
                  padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                  background: '#f1f5f9', color: '#475569',
                }}>{p.label}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>From</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="filter-start-date" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>To</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="filter-end-date" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Operator</label>
              <Select value={operator} onValueChange={setOperator}>
                <SelectTrigger data-testid="filter-operator"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op} value={op}>{op === 'all' ? 'All operators' : op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <form onSubmit={applyCustomerFilter}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Customer contains</label>
              <Input placeholder="e.g. Sosa" value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="filter-customer" />
            </form>
          </div>
        </div>

        {/* Summary stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <StatCard label="Orders" value={summary.count || 0} icon={Package} accent="#7c3aed" />
          <StatCard label="Revenue" value={fmtMoney(summary.total_revenue)} icon={DollarSign} accent="#16a34a" />
          <StatCard label="Units shipped" value={(summary.total_units || 0).toLocaleString()} icon={TrendingUp} accent="#0ea5e9" />
          <StatCard label="Average ticket" value={fmtMoney(summary.average_ticket)} icon={DollarSign} accent="#f59e0b" />
        </div>

        {/* Per-operator + per-customer breakdowns */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 20 }}>
          <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Users size={14} /> By Operator
            </h3>
            <table style={{ width: '100%', marginTop: 10, fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748b' }}>
                  <th style={{ padding: '6px 0', fontWeight: 700 }}>Operator</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Orders</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Units</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_operator || []).map((row) => (
                  <tr key={row.operator} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 0', color: '#0f172a' }}>{row.operator}</td>
                    <td style={{ textAlign: 'right', color: '#475569' }}>{row.orders}</td>
                    <td style={{ textAlign: 'right', color: '#475569' }}>{row.units}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{fmtMoney(row.revenue)}</td>
                  </tr>
                ))}
                {(data.by_operator || []).length === 0 && (
                  <tr><td colSpan="4" style={{ padding: '12px 0', color: '#94a3b8', textAlign: 'center' }}>—</td></tr>
                )}
              </tbody>
            </table>
          </section>

          <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Users size={14} /> By Customer
            </h3>
            <table style={{ width: '100%', marginTop: 10, fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#64748b' }}>
                  <th style={{ padding: '6px 0', fontWeight: 700 }}>Customer</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Orders</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Units</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_customer || [])
                  .sort((a, b) => b.revenue - a.revenue)
                  .slice(0, 10)
                  .map((row) => (
                    <tr key={row.customer} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 0', color: '#0f172a' }}>{row.customer}</td>
                      <td style={{ textAlign: 'right', color: '#475569' }}>{row.orders}</td>
                      <td style={{ textAlign: 'right', color: '#475569' }}>{row.units}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{fmtMoney(row.revenue)}</td>
                    </tr>
                  ))}
                {(data.by_customer || []).length === 0 && (
                  <tr><td colSpan="4" style={{ padding: '12px 0', color: '#94a3b8', textAlign: 'center' }}>—</td></tr>
                )}
              </tbody>
            </table>
          </section>
        </div>

        {/* Orders table */}
        <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 800, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={14} /> Orders ({(data.orders || []).length})
          </h3>
          {loading ? (
            <p style={{ color: '#64748b', textAlign: 'center', padding: 24 }}>Loading…</p>
          ) : (data.orders || []).length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 24 }}>
              No completed orders match the current filters.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', marginTop: 10, fontSize: 13, minWidth: 720 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748b' }}>
                    <th style={{ padding: '8px 0', fontWeight: 700 }}>PO</th>
                    <th style={{ fontWeight: 700 }}>Customer</th>
                    <th style={{ textAlign: 'right', fontWeight: 700 }}>Qty</th>
                    <th style={{ textAlign: 'right', fontWeight: 700 }}>Total</th>
                    <th style={{ fontWeight: 700 }}>Completed</th>
                    <th style={{ fontWeight: 700 }}>Operator</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9' }} data-testid={`report-row-${o.id}`}>
                      <td style={{ padding: '8px 0', color: '#7c3aed', fontWeight: 600 }}>{o.po_number || o.id.slice(0, 8)}</td>
                      <td style={{ color: '#0f172a' }}>{o.customer_name || '—'}</td>
                      <td style={{ textAlign: 'right', color: '#475569' }}>{o.quantity || 0}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{fmtMoney(o.total_amount)}</td>
                      <td style={{ color: '#475569' }}>{fmtDate(o.completed_at)} <span style={{ color: '#94a3b8' }}>{fmtTime(o.completed_at)}</span></td>
                      <td style={{ color: '#475569', fontSize: 12 }}>{o.completed_by || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/proposal/${o.proposal_id}`)} style={{ fontSize: 12, color: '#0ea5e9' }}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default CompletedOrdersReport;
